import React, { useCallback, useContext, useState } from 'react';
import {
  Button,
  Image,
  Link,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Text,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { LoginErrorCard } from './login-error-card';
import { useLoginErrorPageStyles } from './styles';
import MicrosoftIcon from '../media/btn-microsoft.svg';

const HAS_ACCOUNT = 'yes';
const NO_ACCOUNT = 'no';

export type AadEnforcementStep = 'choice' | 'switch' | 'askAdmin';

export type AadEnforcementBackStep = Exclude<AadEnforcementStep, 'choice'>;

export type AadEnforcementPageProps = {
  // Starts the server-side switch flow: a full-page navigation to the server-provided AAD
  // migration URL. Never a fetch — the MSA/AAD auth challenges require top-level navigation.
  // Throws when the switch cannot be started, which surfaces an error next to the button.
  onSwitchAccount: () => void;
  // "Go back" on the first step, which leaves the page. Later steps return to the first step instead.
  onGoBack: () => void;
  // eslint-disable-next-line no-unused-vars
  onBackToChoice?: (fromStep: AadEnforcementBackStep) => void;
  // eslint-disable-next-line no-unused-vars
  onSelectHasWorkAccount?: (hasWorkAccount: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onLearnMore?: (fromStep: AadEnforcementStep) => void;
};

export const AadEnforcementPage: React.FC<AadEnforcementPageProps> = ({
  onSwitchAccount,
  onGoBack,
  onBackToChoice,
  onSelectHasWorkAccount,
  onLearnMore,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext) as { CurrentLocale?: string };
  const classes = useLoginErrorPageStyles();
  const [step, setStep] = useState<AadEnforcementStep>('choice');
  const [hasWorkAccount, setHasWorkAccount] = useState(true);
  const [switchFailed, setSwitchFailed] = useState(false);

  // Help center deep-links are locale-scoped (…/apex/ads/{locale}/…). Derive the language segment
  // from the server-provided CurrentLocale (e.g. "en-US" -> "en"), defaulting to English.
  const locale = (serverContext?.CurrentLocale || 'en-us').toLowerCase().split('-')[0];
  const learnMoreUrl = `https://help.ads.microsoft.com/#apex/ads/${locale}/60043/-1`;

  const handleNext = useCallback(() => {
    if (onSelectHasWorkAccount) {
      onSelectHasWorkAccount(hasWorkAccount);
    }
    setStep(hasWorkAccount ? 'switch' : 'askAdmin');
  }, [hasWorkAccount, onSelectHasWorkAccount]);

  // A successful switch navigates away, so only the failure needs to be reflected in the UI.
  const handleSwitchAccount = useCallback(() => {
    try {
      onSwitchAccount();
      setSwitchFailed(false);
    } catch {
      setSwitchFailed(true);
    }
  }, [onSwitchAccount]);

  const handleBackToChoice = useCallback(() => {
    if (onBackToChoice && step !== 'choice') {
      onBackToChoice(step);
    }
    setSwitchFailed(false);
    setStep('choice');
  }, [onBackToChoice, step]);

  const renderGoBack = (onClick: () => void) => (
    <Button className="login-aad-enforcement-go-back" appearance="secondary" onClick={onClick}>
      {i18n.getString(_TL_('Go back'))}
    </Button>
  );

  const renderLearnMore = () => (
    <Link
      className="login-aad-enforcement-learn-more"
      href={learnMoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onLearnMore && onLearnMore(step)}
      inline
    >
      {i18n.getString(_TL_('Learn more'))}
    </Link>
  );

  if (step === 'askAdmin') {
    return (
      <LoginErrorCard
        idPrefix="login-aad-enforcement"
        title={i18n.getString(_TL_('You need a Microsoft work or school account'))}
        actions={renderGoBack(handleBackToChoice)}
      >
        <Text block>
          {i18n.getString(_TL_('Contact your organization\'s IT administrator to request one. After you receive it, sign in to continue.'))}
          {' '}
          {renderLearnMore()}
        </Text>
      </LoginErrorCard>
    );
  }

  if (step === 'switch') {
    return (
      <LoginErrorCard
        idPrefix="login-aad-enforcement"
        title={i18n.getString(_TL_('Switch to your Microsoft work or school account'))}
        actions={(
          <>
            <Button
              className="login-aad-enforcement-switch"
              appearance="primary"
              icon={<Image src={MicrosoftIcon} width={16} height={16} alt="" aria-hidden="true" />}
              onClick={handleSwitchAccount}
            >
              {i18n.getString(_TL_('Switch accounts'))}
            </Button>
            {renderGoBack(handleBackToChoice)}
          </>
        )}
      >
        <Text block>
          {i18n.getString(_TL_('Use this account each time you sign in to Microsoft Advertising.'))}
          {' '}
          {renderLearnMore()}
        </Text>
        {switchFailed ? (
          <MessageBar className="login-aad-enforcement-switch-error" intent="error">
            <MessageBarBody>
              {i18n.getString(_TL_('We can\'t start the account switch right now. Refresh the page to try again, and contact support if this keeps happening.'))}
            </MessageBarBody>
          </MessageBar>
        ) : null}
      </LoginErrorCard>
    );
  }

  return (
    <LoginErrorCard
      idPrefix="login-aad-enforcement"
      title={i18n.getString(_TL_('Microsoft work or school account required'))}
      actions={(
        <>
          <Button className="login-aad-enforcement-next" appearance="primary" onClick={handleNext}>
            {i18n.getString(_TL_('Continue'))}
          </Button>
          {renderGoBack(onGoBack)}
        </>
      )}
    >
      <Text block>
        {i18n.getString(_TL_('Your ad accounts require Microsoft Entra sign-in.'))}
      </Text>
      {renderLearnMore()}
      <RadioGroup
        className={classes.choiceField}
        value={hasWorkAccount ? HAS_ACCOUNT : NO_ACCOUNT}
        onChange={(_, data) => setHasWorkAccount(data.value === HAS_ACCOUNT)}
      >
        <Radio
          className="login-aad-enforcement-has-account"
          value={HAS_ACCOUNT}
          label={i18n.getString(_TL_('I have a Microsoft work or school account'))}
        />
        <Radio
          className="login-aad-enforcement-no-account"
          value={NO_ACCOUNT}
          label={i18n.getString(_TL_('I need a Microsoft work or school account'))}
        />
      </RadioGroup>
    </LoginErrorCard>
  );
};
